/**
 * Spec 129 — shared brand sharing/lock controls.
 *
 * Extracted from the former BrandsPage `SharingPanel` (spec 076) so the
 * edit-brand modal (`BrandForm`) can host the same lock/unlock + member
 * management UI. BrandsPage was retired by spec 129 and deleted by spec
 * 237; this is now
 * the single home for the sharing controls.
 *
 * Backend endpoints (unchanged from spec 076):
 *   POST   /api/v1/tenants/:tenantId/brands/:brandId/lock      (body {})
 *   DELETE /api/v1/tenants/:tenantId/brands/:brandId/lock
 *   GET    /api/v1/tenants/:tenantId/brands/:brandId/members
 *   POST   /api/v1/tenants/:tenantId/brands/:brandId/members   (body { email })
 *   DELETE /api/v1/tenants/:tenantId/brands/:brandId/members/:email
 *
 * Styled compact for the modal width (max-w-lg).
 */
import { useState, useEffect, useCallback } from 'react';
import { Icon, Spinner } from '../ui/Icon.js';
import { Button } from '../ui/Button.js';
import { apiGet } from '../../lib/api.js';
import { apiPost, apiDelete } from '../../lib/api-mutations.js';
import { useToast } from '../../hooks/useToast.js';
import type { Brand, BrandMembersResponse, BrandMember } from '../../types/index.js';

interface BrandSharingSectionProps {
  brand: Brand;
  /** Called after any lock/unlock/member mutation so callers can refetch. */
  onChange: () => void;
}

export function BrandSharingSection({ brand, onChange }: BrandSharingSectionProps) {
  const { addToast } = useToast();
  const [members, setMembers] = useState<BrandMember[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [lockOwner, setLockOwner] = useState<string | null>(brand.locked_by_email ?? null);

  const tenantId = brand.tenant_id;
  const isLocked = !!brand.locked_by_email;
  const isOwner = !!brand.is_locked_by_me;

  const canLoadMembers = isLocked; // owner or member

  const loadMembers = useCallback(async () => {
    if (!canLoadMembers) {
      setMembers(null);
      return;
    }
    setLoading(true);
    try {
      const res = await apiGet<BrandMembersResponse>(
        `/api/v1/tenants/${tenantId}/brands/${brand.id}/members`,
      );
      setMembers(res.data.members);
      setLockOwner(res.data.locked_by_email);
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to load members', 'error');
    } finally {
      setLoading(false);
    }
  }, [tenantId, brand.id, canLoadMembers, addToast]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const handleLock = async () => {
    try {
      await apiPost(`/api/v1/tenants/${tenantId}/brands/${brand.id}/lock`, {});
      addToast(`Locked "${brand.name}"`, 'success');
      onChange();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to lock brand', 'error');
    }
  };

  const handleUnlock = async () => {
    try {
      await apiDelete(`/api/v1/tenants/${tenantId}/brands/${brand.id}/lock`);
      addToast(`Unlocked "${brand.name}"`, 'success');
      onChange();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to unlock brand', 'error');
    }
  };

  const handleAddMember = async (e?: { preventDefault: () => void }) => {
    e?.preventDefault();
    const trimmed = newEmail.trim();
    if (!trimmed) return;
    try {
      await apiPost(`/api/v1/tenants/${tenantId}/brands/${brand.id}/members`, {
        email: trimmed,
      });
      addToast(`Added ${trimmed}`, 'success');
      setNewEmail('');
      onChange();
      loadMembers();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to add member', 'error');
    }
  };

  const handleRemove = async (email: string) => {
    try {
      await apiDelete(
        `/api/v1/tenants/${tenantId}/brands/${brand.id}/members/${encodeURIComponent(email)}`,
      );
      addToast(`Removed ${email}`, 'success');
      onChange();
      loadMembers();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to remove member', 'error');
    }
  };

  return (
    <section data-testid="sharing-panel" className="hub-section">
      <div className="hub-section__head">
        <h2>Sharing</h2>
      </div>
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
        <div className="flex items-start justify-between gap-3">
          <p className="text-xs text-slate-500">
            {!isLocked &&
              'This brand is open — any authenticated user can see and edit it.'}
            {isLocked &&
              isOwner &&
              'You locked this brand. Only you and the members below can access it.'}
            {isLocked &&
              !isOwner &&
              `Locked by ${lockOwner ?? 'another user'}. You have read access as a collaborator.`}
          </p>
          {!isLocked && (
            <Button variant="ghost" onClick={handleLock} data-testid="brand-lock">
              <Icon name="bx-lock" /> Lock this brand
            </Button>
          )}
          {isLocked && isOwner && (
            <Button variant="ghost" onClick={handleUnlock} data-testid="brand-unlock">
              <Icon name="bx-lock-open" /> Unlock
            </Button>
          )}
        </div>

        {isLocked && (
          <div className="mt-3">
            <div className="mb-1.5 text-xs font-semibold text-slate-500">
              Members
            </div>
            {loading ? (
              <Spinner className="text-violet-500" />
            ) : (
              <ul
                className="divide-y divide-slate-100 rounded-md border border-slate-200 bg-white"
                data-testid="members-list"
              >
                <li className="flex items-center justify-between px-2.5 py-1.5 text-xs">
                  <span className="font-medium text-slate-700">
                    {lockOwner ?? brand.locked_by_email}
                  </span>
                  <span className="text-xs text-violet-600">owner</span>
                </li>
                {(members ?? []).map((m) => (
                  <li
                    key={m.email}
                    className="flex items-center justify-between px-2.5 py-1.5 text-xs"
                  >
                    <span className="text-slate-700">{m.email}</span>
                    {isOwner && (
                      <button
                        type="button"
                        onClick={() => handleRemove(m.email)}
                        className="flex items-center gap-1 text-xs text-rose-600 hover:text-rose-800"
                        data-testid={`remove-member-${m.email}`}
                      >
                        <Icon name="bx-trash" /> Remove
                      </button>
                    )}
                  </li>
                ))}
                {(members ?? []).length === 0 && (
                  <li className="px-2.5 py-1.5 text-xs text-slate-400">
                    No other members yet
                  </li>
                )}
              </ul>
            )}

            {isOwner && (
              <div className="mt-2 flex items-center gap-2" data-testid="add-member-form">
                <input
                  type="email"
                  placeholder="collaborator@example.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleAddMember();
                    }
                  }}
                  className="flex-1 rounded-md border border-slate-300 px-2.5 py-1 text-xs focus:border-violet-500 focus:ring-1 focus:ring-violet-500 focus:outline-none"
                  data-testid="add-member-input"
                />
                <Button variant="ghost" onClick={() => void handleAddMember()} data-testid="add-member-btn">
                  <Icon name="bx-user-plus" /> Add
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
